import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { Play, Music, Plus, X, Trash2, Video, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", platform: "youtube", embed_url: "", category: "wellness", order: 0 });
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/media/content");
      setItems(res.data.items || []);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => {
    setForm({ title: "", description: "", platform: "youtube", embed_url: "", category: "wellness", order: 0 });
    setShowForm(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post("/admin/media/content", form);
      resetForm();
      fetchItems();
    } catch (err) { /* silent */ }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this media item?")) return;
    try { await apiClient.delete(`/admin/media/content/${id}`); fetchItems(); }
    catch (err) { /* silent */ }
  };

  const youtube = items.filter(i => i.platform === "youtube");
  const spotify = items.filter(i => i.platform === "spotify");

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-media">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white mb-1">Media & Streaming</h1>
            <p className="text-white/40 text-sm">Manage YouTube videos and Spotify playlists — {items.length} items</p>
          </div>
          <Button onClick={() => showForm ? resetForm() : setShowForm(true)} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl gap-2" data-testid="new-media-btn">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Cancel" : "Add Media"}
          </Button>
        </div>

        {showForm && (
          <form onSubmit={handleCreate} className="bg-[#161821] border border-white/5 rounded-2xl p-6 mb-6 space-y-4" data-testid="media-form">
            <div className="flex gap-2 mb-2">
              {["youtube", "spotify"].map(p => (
                <button key={p} type="button" onClick={() => setForm({...form, platform: p})}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-all ${form.platform === p ? (p === "youtube" ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-green-500/20 text-green-400 border border-green-500/30") : "bg-white/5 text-white/40 border border-white/5"}`}
                  data-testid={`platform-${p}`}
                >
                  {p === "youtube" ? <Video className="w-4 h-4" /> : <Music className="w-4 h-4" />}
                  {p === "youtube" ? "YouTube" : "Spotify"}
                </button>
              ))}
            </div>
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Title..." className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" required data-testid="media-title" />
            <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Description..." className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" data-testid="media-description" />
            <Input value={form.embed_url} onChange={(e) => setForm({...form, embed_url: e.target.value})} placeholder={form.platform === "youtube" ? "YouTube embed URL (https://www.youtube.com/embed/...)" : "Spotify embed URL (https://open.spotify.com/embed/...)"} className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" required data-testid="media-url" />
            <div className="flex gap-3 items-center">
              <Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} placeholder="Category (e.g., wellness, therapy)" className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl max-w-xs" data-testid="media-category" />
              <Input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} placeholder="Order" className="bg-[#0f1117] border-white/10 text-white rounded-xl w-24" data-testid="media-order" />
              <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl ml-auto" data-testid="media-submit">
                {saving ? "Adding..." : "Add Media"}
              </Button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-8">
            {/* YouTube Section */}
            <div>
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Video className="w-5 h-5 text-red-400" /> YouTube Videos ({youtube.length})
              </h2>
              {youtube.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {youtube.map(item => (
                    <div key={item.media_id} className="bg-[#161821] border border-white/5 rounded-2xl overflow-hidden" data-testid={`media-${item.media_id}`}>
                      <div className="aspect-video">
                        <iframe src={item.embed_url} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen title={item.title} />
                      </div>
                      <div className="p-4 flex items-start justify-between">
                        <div>
                          <h3 className="text-white text-sm font-medium">{item.title}</h3>
                          <p className="text-white/40 text-xs mt-1">{item.category}</p>
                        </div>
                        <button onClick={() => handleDelete(item.media_id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/20 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-white/30 text-sm">No YouTube videos yet</p>}
            </div>

            {/* Spotify Section */}
            <div>
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Music className="w-5 h-5 text-green-400" /> Spotify Playlists ({spotify.length})
              </h2>
              {spotify.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {spotify.map(item => (
                    <div key={item.media_id} className="bg-[#161821] border border-white/5 rounded-2xl overflow-hidden" data-testid={`media-${item.media_id}`}>
                      <iframe src={item.embed_url} className="w-full h-[152px]" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" title={item.title} />
                      <div className="p-4 flex items-start justify-between">
                        <div>
                          <h3 className="text-white text-sm font-medium">{item.title}</h3>
                          <p className="text-white/40 text-xs mt-1">{item.description}</p>
                        </div>
                        <button onClick={() => handleDelete(item.media_id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/20 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-white/30 text-sm">No Spotify playlists yet</p>}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
